import { Dispatch } from "redux";
import {
  DELETE_NOTIFICATION,
  CLEAR_NOTIFICATIONS,
  CLEAR_ERRORS,
  CREATE_NOTIFICATION,
} from "../const";
import {
  ClearErrorsAction,
  ClearNotificationAction,
  CreateNotificationAction,
  DeleteNotificationAction,
} from "../../types";
import {
  isAuthError,
  isReauthenticating,
} from "../../providers/auth0-instance";

export const displaySuccessNotification =
  (message: string) => async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id: Date.now(),
        message,
        severity: "success",
      },
    });

export const displayInfoNotification =
  (message: string) => async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id: Date.now(),
        message,
        severity: "info",
      },
    });

export const displayWarningNotification =
  (message: string) => async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id: Date.now(),
        message,
        severity: "warning",
      },
    });

export const displayErrorNotification =
  (message: string) => async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id: Date.now(),
        message,
        severity: "error",
        anchor: { vertical: "top", horizontal: "center" },
      },
    });

export const displayPermanentErrorNotification =
  (message: string) => async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id: Date.now(),
        message,
        severity: "error",
        autoHideDuration: null,
        anchor: { vertical: "top", horizontal: "center" },
      },
    });

export const displayPermanentWarningNotification =
  (message: string, id: number) =>
  async (dispatch: Dispatch<CreateNotificationAction>) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: {
        id,
        message,
        severity: "warning",
        autoHideDuration: null,
        anchor: { vertical: "bottom", horizontal: "center" },
      },
    });

export const closeNotification =
  (id: number) => async (dispatch: Dispatch<DeleteNotificationAction>) =>
    dispatch({ type: DELETE_NOTIFICATION, payload: id });

export const clearNotifications =
  () => async (dispatch: Dispatch<ClearNotificationAction>) =>
    dispatch({ type: CLEAR_NOTIFICATIONS });

export const clearErrors =
  () => async (dispatch: Dispatch<ClearErrorsAction>) =>
    dispatch({ type: CLEAR_ERRORS });

interface ErrorHandlerContext {
  dispatch: any;
  queryFulfilled: Promise<any>;
}

// Only reached when signing the user back in automatically was declined or did
// not happen - an expired session normally leaves for Auth0 without saying
// anything, because there is nothing for the user to do about it.
export const SESSION_EXPIRED_MESSAGE =
  "We could not sign you back in. Please reload the page to try again.";

// Shown once per page load. When the token is gone, every query in the opening
// burst fails the same way, and one bad moment should not produce six identical
// notifications.
let sessionErrorReported = false;

/**
 * Report a failed query.
 *
 * A request that never left the browser because the session has gone has told
 * us nothing about the endpoint - so it does not get the endpoint's message. The
 * user's problem there is their session, and saying "Failed to list devices"
 * both misnames it and repeats it once per query in flight. A token request that
 * failed for any other reason, such as the network, is not marked as an auth
 * error and gets the endpoint's message like any request that never arrived.
 *
 * `alwaysNotify` opts a caller out of that once-per-page-load suppression. The
 * suppression exists for the opening read burst, which nobody asked for; a user
 * who just pressed Save did ask, and a save that does nothing and says nothing
 * is worse than a repeated message. It does not opt out of the redirect check
 * below: when the page is already on its way to Auth0 there is nothing for any
 * caller to add.
 */
export const notifyQueryError = (
  dispatch: any,
  message: string,
  rejection: any,
  { alwaysNotify = false }: { alwaysNotify?: boolean } = {},
) => {
  if (isAuthError(rejection?.error)) {
    // A sign-in redirect is already taking the user to Auth0. The page is
    // leaving, so this is a message nobody will finish reading - and the one
    // thing it could tell them to do is already happening.
    if (isReauthenticating()) return;
    if (sessionErrorReported && !alwaysNotify) return;
    sessionErrorReported = true;
    dispatch(displayErrorNotification(SESSION_EXPIRED_MESSAGE));
    return;
  }
  dispatch(displayErrorNotification(message));
};

/**
 * User-initiated endpoints pass `{ alwaysNotify: true }`: they only run because
 * someone pressed something, and several of their call sites have no other
 * failure UI, so a swallowed auth error is a control that does nothing and says
 * nothing. The line is user-pressed against automatic, not mutation against
 * query - a lazy query behind a dropdown is on the near side of it. Endpoints
 * that fire by themselves stay on the plain form; they are the opening burst
 * the guard is for.
 */
export const withErrorNotification =
  (message: string, options?: { alwaysNotify?: boolean }) =>
  async (_arg: unknown, { dispatch, queryFulfilled }: ErrorHandlerContext) => {
    try {
      await queryFulfilled;
    } catch (err) {
      notifyQueryError(dispatch, message, err, options);
    }
  };
